import { Outlet, NavLink } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { fetchMeta } from '../lib/api-client';
import { auth, logoutFirebase, onAuthStateChanged } from '../lib/firebase';
import type { Operator, Terminal, Location } from '../types';

interface ReferenceData {
  operators: Operator[];
  terminals: Terminal[];
  locations: Location[];
  loading: boolean;
  error: string | null;
}

const ReferenceDataContext = createContext<ReferenceData>({
  operators: [],
  terminals: [],
  locations: [],
  loading: true,
  error: null,
});

export function useReferenceData() {
  return useContext(ReferenceDataContext);
}

interface StatusMessage {
  type: 'success' | 'error' | 'info';
  text: string;
}

const StatusContext = createContext<{
  message: StatusMessage | null;
  setMessage: (msg: StatusMessage | null) => void;
}>({
  message: null,
  setMessage: () => {},
});

export function useStatus() {
  return useContext(StatusContext);
}

const navLinkCls = ({ isActive }: { isActive: boolean }) =>
  `px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
    isActive
      ? 'border-blue-600 text-blue-600'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
  }`;

export function TabLayout() {
  const [user, setUser] = useState<{ email: string; name: string; picture?: string } | null>(null);
  const [refData, setRefData] = useState<ReferenceData>({
    operators: [],
    terminals: [],
    locations: [],
    loading: true,
    error: null,
  });
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setUser({ email: fbUser.email || '', name: fbUser.displayName || '', picture: fbUser.photoURL || '' });
      } else {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    fetchMeta()
      .then((data) => {
        setRefData({
          operators: data.operators,
          terminals: data.terminals,
          locations: data.locations,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        setRefData((prev) => ({
          ...prev,
          loading: false,
          error: err.message,
        }));
      });
  }, []);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const handleLogout = async () => {
    await logoutFirebase();
    setUser(null);
    setStatusMessage({ type: 'info', text: 'Logged out' });
  };

  return (
    <ReferenceDataContext.Provider value={refData}>
      <StatusContext.Provider value={{ message: statusMessage, setMessage: setStatusMessage }}>
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-2">
                <img src="/etigo-logo.png" alt="Etigo" className="w-8 h-8 rounded-md" />
                <h1 className="text-lg font-semibold text-gray-800">Etigo Tool</h1>
              </div>
              <div className="flex items-center gap-3">
                {user && (
                  <>
                    {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
                    <span className="text-sm text-gray-600 hidden sm:inline">{user.name}</span>
                    <button onClick={handleLogout} className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md text-sm cursor-pointer transition-colors">
                      Logout
                    </button>
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Tab Navigation */}
          <nav className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto flex">
              <NavLink to="/directions" className={navLinkCls}>
                Terminal Directions
              </NavLink>
              <NavLink to="/routes" className={navLinkCls}>
                Routes & Trips
              </NavLink>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </main>

          {/* Toast Notification */}
          {statusMessage && (
            <div
              className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium max-w-sm transition-all ${
                statusMessage.type === 'success'
                  ? 'bg-green-600'
                  : statusMessage.type === 'error'
                  ? 'bg-red-600'
                  : 'bg-blue-600'
              }`}
            >
              {statusMessage.text}
            </div>
          )}
        </div>
      </StatusContext.Provider>
    </ReferenceDataContext.Provider>
  );
}
